/* =========================================================================
   Installment Payment Widget  —  vanilla JS, no dependencies.

   - Registered as the custom element <installment-widget> so it transparently
     re-initialises whenever the theme swaps the product DOM (variant change
     with section render, quick view, product swap, etc.).
   - Reads its configuration from a JSON <script class="iw-config"> child.
   - Reacts to AJAX variant updates through, in priority order:
        1. FoxTheme pub/sub  'variant-change'  (Hyper / OS 2.0 native)
        2. document 'variant:changed' CustomEvent
        3. MutationObserver on <variant-selects [data-selected-variant]>
     so prices stay in sync with zero flicker and no duplicate listeners.
   ========================================================================= */
(function () {
  'use strict';

  if (customElements.get('installment-widget')) return;

  /** Format an integer amount of cents into a money string using a
   *  Shopify-style "{{amount}}" template. Installments are shown with no
   *  decimals to match the reference design (e.g. "EGP 7,885"). */
  function formatMoney(cents, template) {
    var major = Math.round(Number(cents) / 100);
    var withSeparators = String(major).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return template.replace(/\{\{\s*amount[\w]*\s*\}\}/, withSeparators);
  }

  /** Safely parse the JSON config embedded in the element. */
  function readConfig(root) {
    var node = root.querySelector('script.iw-config');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.warn('[installment-widget] Invalid config JSON', err);
      return null;
    }
  }

  /** Return only the enabled providers, preserving declaration order. */
  function enabledProviders(config) {
    return Object.keys(config.providers)
      .map(function (key) {
        return Object.assign({ key: key }, config.providers[key]);
      })
      .filter(function (p) {
        return p.enabled !== false && Array.isArray(p.months) && p.months.length;
      });
  }

  class InstallmentWidget extends HTMLElement {
    connectedCallback() {
      // Guard against double-initialisation if the node is moved around.
      if (this._initialised) return;
      this._initialised = true;

      this.config = readConfig(this);
      if (!this.config) return;

      this.providers = enabledProviders(this.config);
      if (!this.providers.length) return;

      this.sectionId = this.dataset.sectionId || '';
      this.priceCents = parseInt(this.dataset.price, 10) || 0;
      this.activeProviderKey = this.providers[0].key;

      this.cacheDom();
      this.buildTabsAndPanels();
      this.bindEvents();
      this.subscribeToVariantChanges();
      this.render();
    }

    disconnectedCallback() {
      this.teardown();
      this._initialised = false;
    }

    /* --------------------------------------------------------------- DOM */
    cacheDom() {
      this.badge = this.querySelector('[data-iw-badge]');
      this.lowestEl = this.querySelector('[data-iw-lowest]');
      this.modal = this.querySelector('[data-iw-modal]');
      this.tabsEl = this.querySelector('[data-iw-tabs]');
      this.panelsEl = this.querySelector('[data-iw-panels]');
    }

    /** Build provider tabs + plan panels from config (no duplicated markup). */
    buildTabsAndPanels() {
      var self = this;
      var tabsFrag = document.createDocumentFragment();
      var panelsFrag = document.createDocumentFragment();

      this.providers.forEach(function (provider, index) {
        var isActive = provider.key === self.activeProviderKey;

        // Tab (logo image when supplied, otherwise styled wordmark text)
        var tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'iw-tab' + (isActive ? ' is-active' : '');
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.dataset.iwProvider = provider.key;
        if (provider.logo) {
          var img = document.createElement('img');
          img.src = provider.logo;
          img.alt = provider.label || provider.key;
          img.loading = 'lazy';
          tab.appendChild(img);
        } else {
          tab.textContent = provider.label || provider.key;
        }
        tabsFrag.appendChild(tab);

        // Panel + table
        var panel = document.createElement('div');
        panel.className = 'iw-panel' + (isActive ? ' is-active' : '');
        panel.setAttribute('role', 'tabpanel');
        panel.dataset.iwPanel = provider.key;
        panel.innerHTML = self.tableMarkup();
        panelsFrag.appendChild(panel);
      });

      this.tabsEl.appendChild(tabsFrag);
      this.panelsEl.appendChild(panelsFrag);
    }

    tableMarkup() {
      var s = this.config.strings || {};
      return (
        '<div class="iw-table">' +
        '<div class="iw-table__head">' +
        '<span>' + (s.duration || 'Duration') + '</span>' +
        '<span>' + (s.interest || 'Interest') + '</span>' +
        '<span class="iw-th--price">' + (s.monthly || 'Monthly payment') + '</span>' +
        '</div>' +
        '<div data-iw-rows></div>' +
        '</div>'
      );
    }

    /* ------------------------------------------------------------ Events */
    bindEvents() {
      this._onBadgeClick = this.open.bind(this);
      this._onKeydown = function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') this.close();
      }.bind(this);
      this._onModalClick = function (e) {
        // Close on the close button OR any click outside the dialog (overlay /
        // surrounding backdrop area) — clicks inside the dialog are ignored.
        if (e.target.closest('[data-iw-close]') || !e.target.closest('.iw-modal__dialog')) {
          this.close();
        }
      }.bind(this);
      this._onTabClick = function (e) {
        var tab = e.target.closest('[data-iw-provider]');
        if (tab) this.selectProvider(tab.dataset.iwProvider);
      }.bind(this);

      this.badge.addEventListener('click', this._onBadgeClick);
      this.modal.addEventListener('click', this._onModalClick);
      this.tabsEl.addEventListener('click', this._onTabClick);
    }

    /* -------------------------------------------------- Variant tracking */
    subscribeToVariantChanges() {
      var self = this;

      // 1) Native Hyper/OS 2.0 pub/sub (most reliable, carries fresh variant)
      if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
        this._unsubscribe = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          function (payload) {
            var data = payload && payload.data;
            if (!data || !data.variant) return;
            if (self.sectionId && String(data.sectionId) !== String(self.sectionId)) return;
            self.updatePrice(data.variant.price);
          }
        );
      }

      // 2) Fallback: document-level CustomEvent
      this._onVariantChanged = function (e) {
        var variant = e.detail && e.detail.variant;
        if (variant && typeof variant.price !== 'undefined') self.updatePrice(variant.price);
      };
      document.addEventListener('variant:changed', this._onVariantChanged);

      // 3) Last-resort fallback: observe the selected-variant JSON in the DOM.
      var productInfo = this.closest('product-info') || document;
      var variantSource = productInfo.querySelector
        ? productInfo.querySelector('variant-selects [data-selected-variant]')
        : null;
      if (variantSource) {
        this._observer = new MutationObserver(function () {
          try {
            var v = JSON.parse(variantSource.textContent);
            if (v && typeof v.price !== 'undefined') self.updatePrice(v.price);
          } catch (err) {
            /* ignore malformed transient states */
          }
        });
        this._observer.observe(variantSource, { childList: true, characterData: true, subtree: true });
      }
    }

    updatePrice(cents) {
      cents = parseInt(cents, 10);
      if (isNaN(cents) || cents === this.priceCents) return; // no stale work / no redundant repaint
      this.priceCents = cents;
      this.render();
    }

    /* ------------------------------------------------------- Calculation */
    /** Provider-adjusted monthly installment, in cents.
     *  Total payable = price + (price × rate%); monthly = total / months. */
    monthlyCents(percentage, months) {
      var rate = Number(percentage) || 0;
      var total = this.priceCents * (1 + rate / 100);
      return total / months;
    }

    /** Lowest monthly payment across every enabled provider and duration. */
    lowestMonthlyCents() {
      var self = this;
      var lowest = Infinity;
      this.providers.forEach(function (provider) {
        provider.months.forEach(function (m) {
          var monthly = self.monthlyCents(provider.percentage, m);
          if (monthly < lowest) lowest = monthly;
        });
      });
      return isFinite(lowest) ? lowest : 0;
    }

    /* ----------------------------------------------------------- Render */
    render() {
      this.renderBadge();
      this.renderActivePanel();
    }

    renderBadge() {
      if (!this.lowestEl) return;
      this.lowestEl.textContent = formatMoney(this.lowestMonthlyCents(), this.config.moneyFormat);
    }

    renderActivePanel() {
      var provider = this.providers.find(
        function (p) { return p.key === this.activeProviderKey; }.bind(this)
      );
      if (!provider) return;

      var panel = this.panelsEl.querySelector('[data-iw-panel="' + provider.key + '"]');
      var rowsHost = panel && panel.querySelector('[data-iw-rows]');
      if (!rowsHost) return;

      var self = this;
      var s = this.config.strings || {};
      var monthLabel = s.months || 'months';
      var perMonth = s.perMonth || '/ mo';
      var rate = (Number(provider.percentage) || 0) + '%';
      var moneyFormat = this.config.moneyFormat;

      rowsHost.innerHTML = provider.months
        .map(function (m) {
          var monthly = formatMoney(self.monthlyCents(provider.percentage, m), moneyFormat);
          var unit = m === 1 ? (s.month || 'month') : monthLabel;
          return (
            '<div class="iw-row">' +
            '<span class="iw-row__duration">' + m + ' ' + unit + '</span>' +
            '<span><span class="iw-pill">' + rate + '</span></span>' +
            '<span class="iw-row__price">' + monthly + ' ' + perMonth + '</span>' +
            '</div>'
          );
        })
        .join('');
    }

    /* ----------------------------------------------------------- Actions */
    selectProvider(key) {
      if (key === this.activeProviderKey) return;
      this.activeProviderKey = key;

      this.tabsEl.querySelectorAll('[data-iw-provider]').forEach(function (tab) {
        var on = tab.dataset.iwProvider === key;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.panelsEl.querySelectorAll('[data-iw-panel]').forEach(function (panel) {
        panel.classList.toggle('is-active', panel.dataset.iwPanel === key);
      });

      this.renderActivePanel();
    }

    open() {
      // Guard: never re-enter while already open, otherwise a second open would
      // capture 'hidden' as the value to restore and strand the page scroll.
      if (this.modal.classList.contains('is-open')) return;
      // Render fresh in case the price changed while the modal was closed.
      this.renderActivePanel();
      this.modal.hidden = false;
      // Force reflow so the CSS transition runs from the hidden state.
      void this.modal.offsetWidth;
      this.modal.classList.add('is-open');
      this.badge.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this._onKeydown);
      document.documentElement.classList.add('iw-modal-open');
      this._prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      var firstTab = this.tabsEl.querySelector('.iw-tab');
      if (firstTab) firstTab.focus();
    }

    close() {
      this.modal.classList.remove('is-open');
      this.badge.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this._onKeydown);
      document.documentElement.classList.remove('iw-modal-open');
      document.body.style.overflow = this._prevOverflow || '';
      var self = this;
      // Hide after the transition to keep it out of the a11y tree.
      window.setTimeout(function () {
        if (!self.modal.classList.contains('is-open')) self.modal.hidden = true;
      }, 240);
    }

    teardown() {
      if (this._unsubscribe) this._unsubscribe();
      if (this._observer) this._observer.disconnect();
      document.removeEventListener('variant:changed', this._onVariantChanged);
      document.removeEventListener('keydown', this._onKeydown);
      document.body.style.overflow = this._prevOverflow || '';
      document.documentElement.classList.remove('iw-modal-open');
    }
  }

  customElements.define('installment-widget', InstallmentWidget);
})();
